import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import QuickActions from '../components/dashboard/QuickActions';
import ActiveJobsList from '../components/dashboard/ActiveJobsList';
import './AdminDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const socket = useSocket();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalUsers: 0, serviceProviders: 0, shipOwners: 0, pendingVerifications: 0, openDisputes: 0 });
  const [pendingList, setPendingList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const dashboardRes = await fetch(`${API_BASE}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const dashboardData = await dashboardRes.json();
      
      const pendingRes = await fetch(`${API_BASE}/admin/pending-verifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingData = await pendingRes.json();
      
      if (dashboardData.success) {
        setStats({
          totalUsers: dashboardData.stats.totalUsers || 0,
          serviceProviders: dashboardData.stats.totalVendors || 0,
          shipOwners: dashboardData.stats.totalOwners || 0,
          pendingVerifications: pendingData.count || 0,
          openDisputes: dashboardData.stats.openDisputes || 0
        });
      }
      if (pendingData.success) setPendingList(pendingData.pendingVerifications || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!socket) return;
    socket.on('payment_uploaded', fetchData);
    socket.on('payment_verified', fetchData);
    socket.on('work_completed', fetchData);
    socket.on('payment_released', fetchData);
    socket.on('new_rfq', fetchData);
    socket.on('new_bid', fetchData);
    socket.on('bid_accepted', fetchData);
    socket.on('contract_status_change', fetchData);
    return () => {
      socket.off('payment_uploaded', fetchData);
      socket.off('payment_verified', fetchData);
      socket.off('work_completed', fetchData);
      socket.off('payment_released', fetchData);
      socket.off('new_rfq', fetchData);
      socket.off('new_bid', fetchData);
      socket.off('bid_accepted', fetchData);
      socket.off('contract_status_change', fetchData);
    };
  }, [socket, fetchData]);

  if (loading) return <div className="loading-spinner">Loading dashboard...</div>;

  return (
    <div className="admin-dashboard">
      <div className="welcome-section">
        <h1>Hello, {user?.name?.split(' ')[0] || 'Admin'}!</h1>
        <p>Platform overview and pending tasks</p>
      </div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-icon"><i className="fas fa-users"></i></div><div><div className="stat-value">{stats.totalUsers}</div><div className="stat-label">Total Users</div></div></div>
        <div className="stat-card"><div className="stat-icon"><i className="fas fa-building"></i></div><div><div className="stat-value">{stats.serviceProviders}</div><div className="stat-label">Service Providers</div></div></div>
        <div className="stat-card"><div className="stat-icon"><i className="fas fa-ship"></i></div><div><div className="stat-value">{stats.shipOwners}</div><div className="stat-label">Ship Owners</div></div></div>
        <div className="stat-card pending" onClick={() => navigate('/admin/payment-verifications')}><div className="stat-icon"><i className="fas fa-clock"></i></div><div><div className="stat-value">{stats.pendingVerifications}</div><div className="stat-label">Pending Verifications</div></div></div>
      </div>

      {pendingList.length > 0 && (
        <div className="pending-verifications-section">
          <h3><i className="fas fa-hand-holding-usd"></i> Pending Payment Verifications</h3>
          <div className="pending-list">
            {pendingList.slice(0, 5).map(contract => (
              <div key={contract._id} className="pending-item" onClick={() => navigate(`/admin/payment-verification/${contract._id}`)}>
                <div className="pending-info"><strong>{contract.vesselName}</strong><span>{contract.serviceType}</span></div>
                <div className="pending-details"><span>${contract.totalAmount?.toLocaleString()}</span><span>{contract.ownerId?.company || contract.ownerId?.name}</span></div>
                <button className="view-btn">Verify <i className="fas fa-arrow-right"></i></button>
              </div>
            ))}
          </div>
          {pendingList.length > 5 && <button className="view-all-btn" onClick={() => navigate('/admin/payment-verifications')}>View All ({pendingList.length})</button>}
        </div>
      )}

      <div className="dashboard-grid">
        <div className="dashboard-main"><ActiveJobsList userRole="admin" /></div>
        <div className="dashboard-sidebar"><QuickActions userRole="admin" /></div>
      </div>
    </div>
  );
};

export default AdminDashboard;